<template>
  <section>
    <header class="mb-6 space-y-4">
      <div class="flex items-center space-x-2">
          <span
              class="relative flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white bg-hurst-dark-orange"
              aria-hidden="true">
            3
          </span>
        <h2 class="text-base md:text-2xl text-hurst-light-blue font-semibold">Shipping Address</h2>
      </div>
      <h3 class="text-sm md:text-base text-hurst-blue font-medium">Where would you like your workbook shipped?</h3>
    </header>
    <form @submit.prevent="getShipping" class="space-y-6">
      <p v-if="errors.hasOwnProperty('address')" class="text-red-600">{{ errors.address }}</p>
      <div class="2xl:w-1/2">
        <label v-if="lockedFromAddress && lockedFromAddressReason == 'pobox'" for="shippingaddress1"
               class="block text-sm font-medium leading-5 text-hurst-blue "><sup class="text-red-500">*</sup>Address <span
            class="text-red-600">P.O. Box not allowed.</span></label>
        <label v-else for="shippingaddress1"
               class="block text-sm font-medium leading-5 text-hurst-blue "><sup class="text-red-500">*</sup>Address</label>
        <div class="mt-1 relative rounded-lg shadow-sm">
          <input id="shippingaddress1"
                 type="text"
                 v-model="shipping.addressLine1"
                 required
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                 :class="{'border-2 border-red-600': lockedFromAddress && lockedFromAddressReason == 'pobox'}"/>
        </div>
        <p v-if="errors.hasOwnProperty('addressLine1')">{{ errors.addressLine1 }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="shippingaddress2"
               class="block text-sm font-medium leading-5 text-hurst-blue ">Suite/Apt#/Other</label>
        <div class="mt-1 relative rounded-lg shadow-sm">
          <input id="shippingaddress2"
                 type="text"
                 v-model="shipping.addressLine2"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('addressLine2')">{{ errors.addressLine2 }}</p>
      </div>
      <div class="2xl:w-1/2">
        <div class="relative rounded-lg shadow-sm">
          <label for="shippingcountry" class="block text-sm font-medium leading-5 text-hurst-blue ">
            <sup class="text-red-500">*</sup>Country
          </label>
          <select aria-label="Country"
                  id="shippingcountry"
                  v-model="shipping.country"
                  @change="getStateProvinces"
                  class="form-select mt-1 relative block w-full rounded focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
            <option value="US">United States</option>
            <option disabled="true">------------------------</option>
            <template v-for="country in countries">
              <option v-if="country.country !== 'United States'"
                      :key="country.abbreviation"
                      :value="country.iso">
                {{ country.country }}
              </option>
            </template>
          </select>
        </div>
        <p v-if="errors.hasOwnProperty('country')" class="text-red-600">{{ errors.country }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="shippingcity" class="block text-sm font-medium leading-5 text-hurst-blue ">
          <sup class="text-red-500">*</sup>City
        </label>
        <div class="mt-1 relative rounded-lg shadow-sm">
          <input id="shippingcity"
                 type="text"
                 v-model="shipping.city"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('city')" class="text-red-600">{{ errors.city }}</p>
      </div>

      <div class="2xl:w-1/2">
        <div class="relative rounded-lg shadow-sm">
          <label for="shippingstateProvince" class="block text-sm font-medium leading-5 text-hurst-blue ">
            <sup class="text-red-500">*</sup>State/Province
          </label>
          <select aria-label="State or Province"
                  id="shippingstateProvince"
                  v-model="shipping.state"
                  class="form-select mt-1 relative block w-full rounded focus:z-10 transition ease-in-out duration-150 sm:text-sm sm:leading-5">
            <option v-if="shipping.country === ''" disabled value="">Select Your Country First</option>
            <option v-else disabled value="">Select Your State or Province</option>
            <option v-for="province in provinces"
                    :value="province.abbreviation"
                    :key="province.abbreviation">
              {{ province.province }}
            </option>
          </select>
        </div>
        <p v-if="errors.hasOwnProperty('state')" class="text-red-600">{{ errors.state }}</p>
      </div>
      <div class="2xl:w-1/2">
        <label for="shippingpostalCode" class="block text-sm font-medium leading-5 text-hurst-blue ">
          <sup class="text-red-500">*</sup>Zip/Postal Code
        </label>
        <div class="mt-1 relative rounded-lg shadow-sm">
          <input id="shippingpostalCode"
                 type="text"
                 v-model="shipping.zip"
                 class="appearance-none block w-full px-3 py-2  border border-hurst-blue rounded shadow-inner placeholder-brand-gray-500 focus:outline-none focus:ring-hurst-teal focus:border-hurst-teal transition duration-150 ease-in-out sm:text-sm sm:leading-5"/>
        </div>
        <p v-if="errors.hasOwnProperty('zip')" class="text-red-600">{{ errors.zip }}</p>
      </div>
      <div class="2xl:w-1/2">
        <cart-button type="submit">
          Continue
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </cart-button>
      </div>
    </form>
  </section>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import {debounce} from 'lodash';
import Swal from 'sweetalert2';
import CartButton from "./CartButton.vue";

const {mapState, mapActions} = createNamespacedHelpers('cart');

export default {
  name: "CartShippingForm",
  components: {CartButton},

  data() {
    return {
      countries: [],
      errors: [],
      gettingShipping: false,
      provinces: [],
      shipping: {
        addressLine1: '',
        addressLine2: '',
        country: '',
        city: '',
        state: '',
        zip: '',
        method: ''
      },
      shippingOptions: [],
      lockedFromAddress: false,
      lockedFromAddressReason: '',
    };
  },

  async created() {
    const resp = await axios.get('/ajax/countries');
    this.countries = resp.data;

    const user_country = this.countries.filter((country) => {
      return country.country === this.user.country;
    });

    if (this.cart.shippingAddress === null || !this.cart.shippingAddress.addressLine1) {
      this.shipping.addressLine1 = this.user.addressLine1;
      this.shipping.addressLine2 = this.user.addressLine2;
      this.shipping.country = Array.isArray(user_country) && user_country.length > 0 ? user_country[0].iso : '';
      this.shipping.city = this.user.city;
      this.shipping.state = this.user.state;
      this.shipping.zip = this.user.zip;
    } else {
      this.shipping = this.cart.shippingAddress;
    }
    this.getStateProvinces();
  },

  computed: {
    ...mapState({
      cart: state => state.cart,
      user: state => state.user
    })
  },

  watch: {
    'shipping.addressLine1': _.debounce(function (newAddress, oldAddress) {
      if (this.checkAddress(newAddress)) {
        this.unlockForm();
      } else {
        // Address check failed. Must be pobox error or empty
        this.lockForm(newAddress ? 'pobox' : 'empty');
      }
    }, 500),
  },

  methods: {
    ...mapActions(['setCart']),

    async getShipping() {
      this.gettingShipping = true;
      Swal.fire({
        icon: 'info',
        title: 'Saving your information',
        html: '<p><i class="fal fa-spinner fa-spin"></i></p>',
        allowOutsideClick: false,
        allowEscapeKey: false,
        allowEnterKey: false,
        showConfirmButton: false,
      });
      try {
        const resp = await axios.post('/Checkout/shipping', this.shipping);
        this.setCart(resp.data);
        this.gettingShipping = false;
        Swal.close();
        this.$emit('address-updated')
      } catch (e) {
        this.errors = e.response.data;
        this.gettingShipping = false;
        Swal.close();
      }
    },

    async getStateProvinces() {
      const resp = await axios.get(`/ajax/countries/${this.shipping.country}?isIso=1`);
      this.provinces = resp.data;
    },

    checkAddress(address) {
      const pattern = /^(((p[\s\.]?[o\s][\.]?)\s?)|(post\s?office\s?))((box|bin|b\.?)?\s?(num|number|#)?\s?\d+)/igm;

      // Check if address is empty or a PO Box
      if (pattern.test(address) || !address) {
        return false;
      } else {
        return true;
      }
    },

    lockForm(reason) {
      this.$root.$emit('lockCheckoutButton');
      this.lockedFromAddress = true;

      // Only show the error for po box addresses.
      if (reason == "pobox") {
        this.lockedFromAddressReason = "pobox";
        Swal.fire({
          icon: 'error',
          title: 'P.O. Boxes are not allowed as shipping addresses.',
          allowOutsideClick: true,
          allowEscapeKey: true
        });
      } else {
        this.lockedFromAddressReason = "";
      }

      if (this.cart.hasShipping && !this.cart.needsShipping) {
        this.$emit('edit-shipping');
      }
      return;
    },

    unlockForm() {
      this.$root.$emit('unlockCheckoutButton');
      this.lockedFromAddress = false;
    }
  }
}
</script>

<style scoped>

</style>